import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import {
  Button,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Building2,
  Filter,
  Plus,
  Pencil,
  Trash2,
  X,
} from "lucide-react";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";
import { useCurrencyContext } from "../../context/Currency/CurrencyContext";
import { useDate } from "../../hooks/useDate";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { normalizeDocument } from "../../utils/documentValidator";
import ConfirmationModal from "../../components/ConfirmationModal";
import CompanyModal from "../../components/CompanyModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import MainContainer from "../../components/MainContainer";

const maskEmail = (email) => {
  if (!email || typeof email !== "string" || !email.includes("@")) return email;
  const [userPart, domainPart] = email.split("@");
  const [domainName, ...domainSuffix] = domainPart.split(".");
  const maskPart = (value, visible = 2) => {
    if (!value) return "***";
    return `${value.slice(0, visible)}***`;
  };
  return `${maskPart(userPart)}@${maskPart(domainName)}${
    domainSuffix.length ? `.${domainSuffix.join(".")}` : ""
  }`;
};

const maskPhone = (phone) => {
  const raw = String(phone || "");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return phone;
  if (digits.length <= 4) return "***";
  return `${digits.slice(0, 2)} *****-${digits.slice(-4)}`;
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark =
    theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 0,
      overflowY: "hidden",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    newBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    headerActions: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    filterTriggerBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      height: 36,
      minWidth: 64,
      padding: "0 16px",
      borderRadius: 12,
      border: "none",
      boxSizing: "border-box",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      lineHeight: 1.75,
      cursor: "pointer",
      boxShadow: "0 3px 1px -2px rgba(0,0,0,0.2),0 2px 2px 0 rgba(0,0,0,0.14),0 1px 5px 0 rgba(0,0,0,0.12)",
      transition: "background-color 250ms cubic-bezier(0.4,0,0.2,1), box-shadow 250ms cubic-bezier(0.4,0,0.2,1)",
      "&:hover": {
        background: theme.palette.primary.dark || theme.palette.primary.main,
      },
    },
    filterCountBadge: {
      marginLeft: 4,
      padding: "1px 7px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.22)",
      fontSize: 11,
      fontWeight: 700,
    },
    filterDrawerPaper: {
      width: 360,
      [theme.breakpoints.down("xs")]: { width: "85vw" },
      backgroundColor: isDark ? "#121212" : "#fff",
      backgroundImage: "none",
      boxShadow: "none",
      display: "flex",
      flexDirection: "column",
      borderTopRightRadius: 12,
      borderBottomRightRadius: 12,
      overflow: "hidden",
    },
    filterDrawerHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 16px",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      flex: "0 0 auto",
    },
    filterDrawerTitle: {
      fontWeight: 600,
      fontSize: 15,
      color: "inherit",
    },
    filterDrawerClose: {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      color: "#fff",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.22)",
        color: "#fff",
      },
    },
    filterDrawerContent: {
      flex: "1 1 auto",
      overflowY: "auto",
      padding: theme.spacing(1, 0.5),
      ...(theme.scrollbarStyles || {}),
      "& .MuiOutlinedInput-root": { borderRadius: 12 },
    },
    filterSectionLabel: {
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: isDark ? "#8696a0" : "#5a6a7a",
      padding: theme.spacing(1, 1.5, 0.5),
    },
    filterFieldWrap: {
      padding: "0 12px 8px",
    },
    filterDrawerFooter: {
      flex: "0 0 auto",
      padding: "10px 14px",
      borderTop: `1px solid ${isDark ? "#202632" : "#e8e8e8"}`,
      display: "flex",
      justifyContent: "space-between",
      gap: 8,
    },

    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    nameCell: {
      fontWeight: 600,
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    statusActive: {
      background: isDark ? "rgba(76,175,80,0.15)" : "rgba(76,175,80,0.12)",
      color: isDark ? "#81c784" : "#2e7d32",
    },
    statusInactive: {
      background: isDark ? "rgba(229,57,53,0.18)" : "rgba(229,57,53,0.10)",
      color: isDark ? "#ef9a9a" : "#c62828",
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    dueChipWarn: {
      display: "inline-block",
      marginLeft: 6,
      padding: "1px 7px",
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 700,
      background: isDark ? "rgba(255,193,7,0.18)" : "rgba(255,193,7,0.20)",
      color: isDark ? "#ffd54f" : "#a06600",
    },
    dueChipDanger: {
      display: "inline-block",
      marginLeft: 6,
      padding: "1px 7px",
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 700,
      background: isDark ? "rgba(229,57,53,0.22)" : "rgba(229,57,53,0.14)",
      color: isDark ? "#ef9a9a" : "#c62828",
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      marginRight: 4,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#e53935",
      "&:hover": {
        background: "rgba(229,57,53,0.10)",
        color: "#c62828",
      },
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: isDark ? "#666" : "#999",
      fontSize: 13.5,
    },
  };
});

const Companies = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { ticketSearchParam, clear } = useSearch();
  const { dateToClient, datetimeToClient } = useDate();
  const { formatCurrency } = useCurrencyContext();
  const { list, save, update, remove } = useCompanies();
  const { list: listPlans } = usePlans();

  const [records, setRecords] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("");
  const [dueStartFilter, setDueStartFilter] = useState("");
  const [dueEndFilter, setDueEndFilter] = useState("");

  // Super-admin guard
  useEffect(() => {
    if (!user.super) {
      toast.error(i18n.t("compaies.notifications.noPermission"));
      setTimeout(() => history.push("/"), 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const data = await list();
      setRecords(data || []);
    } catch (e) {
      toast.error(i18n.t("settings.toasts.recordsLoadError"));
    }
    setLoading(false);
  };

  const loadPlans = async () => {
    try {
      const data = await listPlans();
      setPlans(data || []);
    } catch (e) {
      setPlans([]);
    }
  };

  useEffect(() => {
    if (user.super) {
      loadCompanies();
      loadPlans();
    }
    // limpa o termo do header ao montar pra não trazer busca de outra rota
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayRecords = useMemo(() => {
    return records.map((record) => {
      return {
        ...record,
        displayEmail: maskEmail(record.email),
        displayPhone: maskPhone(record.phone),
        displayStatus: record.status,
        displayDueDate: record.dueDate,
        displayPlan: record.plan,
      };
    });
  }, [records]);

  const activeFilterCount = useMemo(() => {
    return (statusFilter !== "all" ? 1 : 0)
      + (planFilter ? 1 : 0)
      + (dueStartFilter || dueEndFilter ? 1 : 0);
  }, [statusFilter, planFilter, dueStartFilter, dueEndFilter]);

  // Filtro local em cima do termo do header
  const filtered = useMemo(() => {
    const term = (ticketSearchParam || "").trim().toLowerCase();
    const termDoc = normalizeDocument(term);
    return displayRecords.filter((c) => {
      const matchesSearch = !term
        || c.name?.toLowerCase().includes(term)
        || c.email?.toLowerCase().includes(term)
        || (termDoc && c.document?.includes(termDoc))
        || c.phone?.includes(term);

      if (!matchesSearch) return false;

      if (statusFilter !== "all") {
        const active = c.status !== false;
        if (statusFilter === "active" && !active) return false;
        if (statusFilter === "inactive" && active) return false;
      }

      if (planFilter && String(c.planId || "") !== String(planFilter)) {
        return false;
      }

      if (dueStartFilter || dueEndFilter) {
        if (!c.dueDate || !moment(c.dueDate).isValid()) return false;
        const dueDate = moment(c.dueDate).startOf("day");
        if (dueStartFilter && dueDate.isBefore(moment(dueStartFilter).startOf("day"))) {
          return false;
        }
        if (dueEndFilter && dueDate.isAfter(moment(dueEndFilter).startOf("day"))) {
          return false;
        }
      }

      return true;
    });
  }, [displayRecords, ticketSearchParam, statusFilter, planFilter, dueStartFilter, dueEndFilter]);

  const handleClearFilters = () => {
    setStatusFilter("all");
    setPlanFilter("");
    setDueStartFilter("");
    setDueEndFilter("");
  };

  const handleNew = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleEdit = (company) => {
    setSelected(company);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelected(null);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadCompanies();
      handleCloseModal();
      toast.success(i18n.t("settings.toasts.operationSuccess"));
    } catch (e) {
      toast.error(i18n.t("settings.toasts.companyOperationError"));
    }
    setLoading(false);
  };

  const handleAskDelete = () => {
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!selected?.id) {
      setConfirmDelete(false);
      return;
    }
    setLoading(true);
    try {
      await remove(selected.id);
      await loadCompanies();
      setConfirmDelete(false);
      handleCloseModal();
      toast.success(i18n.t("compaies.toasts.deleted"));
    } catch (e) {
      toast.error(i18n.t("settings.toasts.operationDeleteError"));
    }
    setLoading(false);
  };

  const renderStatus = (status) => {
    const active = status !== false;
    return (
      <span
        className={`${classes.statusBadge} ${active ? classes.statusActive : classes.statusInactive}`}
      >
        <span
          className={classes.statusDot}
          style={{ background: active ? "#4caf50" : "#e53935" }}
        />
        {active ? i18n.t("compaies.table.yes") : i18n.t("compaies.table.no")}
      </span>
    );
  };

  const renderPlanValue = (company) => {
    if (company.displayPlan?.amount) {
      return formatCurrency(company.displayPlan.amount);
    }
    return formatCurrency(0);
  };

  const renderDueDate = (record) => {
    const dueDate = record.displayDueDate || record.dueDate;
    if (!dueDate || !moment(dueDate).isValid()) return "-";
    const diff = moment(dueDate).startOf("day").diff(moment().startOf("day"), "days");
    let chip = null;
    if (diff <= 0) {
      chip = <span className={classes.dueChipDanger}>{i18n.t("compaies.due.expired") || "Vencida"}</span>;
    } else if (diff <= 5) {
      chip = <span className={classes.dueChipWarn}>{`${diff}d`}</span>;
    }
    return (
      <>
        {dateToClient(dueDate)}
        {chip}
        {record.recurrence ? <div style={{ fontSize: 11, opacity: 0.7 }}>{record.recurrence}</div> : null}
      </>
    );
  };

  if (!user.super) return null;

  return (
    <MainContainer>
      <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <Building2 size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("compaies.title") || "Empresas"}
              <span className={classes.countChip}>{filtered.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("compaies.subtitle") || "Gerencie todas as empresas (tenants) da plataforma"}
            </div>
          </div>
        </div>

        <div className={classes.headerActions}>
          <button
            type="button"
            className={classes.filterTriggerBtn}
            onClick={() => setFilterDrawerOpen(true)}
            title={i18n.t("kanban.filters.title") || "Filtros"}
          >
            <Filter size={16} strokeWidth={1.8} />
            <span>{i18n.t("kanban.filters.title") || "Filtros"}</span>
            {activeFilterCount > 0 && (
              <span className={classes.filterCountBadge}>{activeFilterCount}</span>
            )}
          </button>

          <Button className={classes.newBtn} onClick={handleNew}>
            <Plus size={16} strokeWidth={2} />
            {i18n.t("compaies.actions.new") || "Nova empresa"}
          </Button>
        </div>
      </div>

      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        classes={{ paper: classes.filterDrawerPaper }}
      >
        <div className={classes.filterDrawerHeader}>
          <Typography className={classes.filterDrawerTitle}>
            {i18n.t("kanban.filters.title") || "Filtros"}
          </Typography>
          <IconButton
            size="small"
            aria-label="close-filter"
            className={classes.filterDrawerClose}
            onClick={() => setFilterDrawerOpen(false)}
          >
            <X size={18} />
          </IconButton>
        </div>
        <div className={classes.filterDrawerContent}>
          <Typography className={classes.filterSectionLabel}>
            {i18n.t("compaies.table.active") || "Ativo"}
          </Typography>
          <div className={classes.filterFieldWrap}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{i18n.t("compaies.table.active") || "Ativo"}</InputLabel>
              <Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                label={i18n.t("compaies.table.active") || "Ativo"}
              >
                <MenuItem value="all">{i18n.t("kanban.filters.tabs.all") || "Todos"}</MenuItem>
                <MenuItem value="active">{i18n.t("compaies.table.yes") || "Sim"}</MenuItem>
                <MenuItem value="inactive">{i18n.t("compaies.table.no") || "Não"}</MenuItem>
              </Select>
            </FormControl>
          </div>

          <Typography className={classes.filterSectionLabel}>
            {i18n.t("compaies.table.plan") || "Plano"}
          </Typography>
          <div className={classes.filterFieldWrap}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{i18n.t("compaies.table.plan") || "Plano"}</InputLabel>
              <Select
                value={planFilter}
                onChange={(event) => setPlanFilter(event.target.value)}
                label={i18n.t("compaies.table.plan") || "Plano"}
              >
                <MenuItem value="">{i18n.t("kanban.filters.tabs.all") || "Todos"}</MenuItem>
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <Typography className={classes.filterSectionLabel}>
            {i18n.t("kanban.startDate") || "Data inicial"}
          </Typography>
          <div className={classes.filterFieldWrap}>
            <TextField
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={dueStartFilter}
              onChange={(event) => setDueStartFilter(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>

          <Typography className={classes.filterSectionLabel}>
            {i18n.t("kanban.endDate") || "Data final"}
          </Typography>
          <div className={classes.filterFieldWrap}>
            <TextField
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={dueEndFilter}
              onChange={(event) => setDueEndFilter(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>
        <div className={classes.filterDrawerFooter}>
          <Button
            size="small"
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0}
          >
            {i18n.t("kanban.filters.clearAll") || "Limpar filtros"}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => setFilterDrawerOpen(false)}
            style={{ borderRadius: 12 }}
          >
            {i18n.t("kanban.filters.apply") || "Aplicar"}
          </Button>
        </div>
      </Drawer>

      <Paper className={classes.mainPaper} variant="outlined" elevation={0}>
        <div className={classes.tableScroll}>
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="left">{i18n.t("compaies.table.name")}</TableCell>
                <TableCell align="left">{i18n.t("compaies.table.email")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.phone")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.plan")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.value")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.active")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
                <TableCell align="center">{i18n.t("compaies.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && records.length === 0 ? (
                <TableRowSkeleton columns={9} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className={classes.emptyState}>
                    {ticketSearchParam
                      ? (i18n.t("compaies.empty.search") || "Nenhuma empresa corresponde à busca.")
                      : (i18n.t("compaies.empty.list") || "Nenhuma empresa cadastrada.")}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id} className={classes.tableRow}>
                    <TableCell align="left" className={classes.nameCell}>
                      {row.name || "-"}
                    </TableCell>
                    <TableCell align="left">{row.displayEmail || "-"}</TableCell>
                    <TableCell align="center">{row.displayPhone || "-"}</TableCell>
                    <TableCell align="center">{row.displayPlan?.name || "-"}</TableCell>
                    <TableCell align="center">{renderPlanValue(row)}</TableCell>
                    <TableCell align="center">{renderStatus(row.displayStatus)}</TableCell>
                    <TableCell align="center">{renderDueDate(row)}</TableCell>
                    <TableCell align="center">
                      {row.lastLogin ? datetimeToClient(row.lastLogin) : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        className={classes.iconActionBtn}
                        onClick={() => handleEdit(row)}
                        aria-label="edit"
                      >
                        <Pencil size={16} strokeWidth={1.8} />
                      </IconButton>
                      <IconButton
                        size="small"
                        className={classes.deleteActionBtn}
                        onClick={() => {
                          setSelected(row);
                          setConfirmDelete(true);
                        }}
                        aria-label="delete"
                      >
                        <Trash2 size={16} strokeWidth={1.8} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>

      <CompanyModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        onDelete={handleAskDelete}
        initialValue={selected}
        loading={loading}
      />

      <ConfirmationModal
        title={
          selected
            ? `${i18n.t("compaies.confirmationModal.deleteTitle")} ${selected.name}?`
            : ""
        }
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
      >
        {i18n.t("compaies.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      </div>
    </MainContainer>
  );
};

export default Companies;
